import Ball from "@Components/game/session-results/feedback/visualization/ball";
import { gameToScreenCoords, getBallPosition, VIEWPORT_DIMENSIONS } from "@Components/game/session-results/feedback/visualization/constants";
import FootballPitch from "@Components/game/session-results/feedback/visualization/football-pitch";
import HeatmapRenderer from "@Components/game/session-results/feedback/visualization/heatmap-renderer";
import buildLoadingPlayers from "@Components/game/session-results/feedback/visualization/loading-formation-players";
import PassVisualization from "@Components/game/session-results/feedback/visualization/pass-visualization";
import Player from "@Components/game/session-results/feedback/visualization/player";
import useReplayRenderer from "@hooks/game/activity/session/use-replay-renderer";
import useReplayTeamColors from "@hooks/game/activity/session/use-replay-team-colors";
import { SessionFeedbackModel } from "@hooks/game/activity/session/use-session-feedback-model";
import "@pixi/events";
import { Stage } from "@pixi/react";
import { type ComponentProps, useEffect, useMemo } from "react";

type ReplayStageOptions = ComponentProps<typeof Stage>["options"];
const DEFAULT_STAGE_RESOLUTION = 1;

export interface IReplayPixiStageLayerOptions {
  showHeatmap?: boolean;
  showVisionCone?: boolean;
  showPassVisualization?: boolean;
  /** Pixi stage clear alpha (default 0 = transparent over CSS grass). */
  stageBackgroundAlpha?: number;
}

export interface IReplayPixiStage {
  feedbackModel: SessionFeedbackModel;
  layerOptions?: IReplayPixiStageLayerOptions;
  stageOptions?: ReplayStageOptions;
  onReadyChange?: (isReady: boolean) => void;
}

const ReplayPixiStage = ({ feedbackModel, layerOptions, stageOptions, onReadyChange }: IReplayPixiStage) => {
  const { replay, playback } = feedbackModel;

  const showHeatmap = layerOptions?.showHeatmap ?? playback.showHeatmap;
  const showVisionCone = layerOptions?.showVisionCone ?? playback.showVisionCone;
  const showPassVisualization = layerOptions?.showPassVisualization ?? true;
  const stageBackgroundAlpha = layerOptions?.stageBackgroundAlpha ?? 0;
  const defaultStageOptions = useMemo(
    () => ({
      resolution: Math.min(window.devicePixelRatio || 1, DEFAULT_STAGE_RESOLUTION),
      autoDensity: true,
      antialias: false,
      powerPreference: "high-performance" as const,
      backgroundColor: 0x000000,
      backgroundAlpha: stageBackgroundAlpha,
    }),
    [stageBackgroundAlpha]
  );

  const { characterStates } = useReplayRenderer({
    replayData: replay.replayData,
    progress: playback.progress,
    isPlaying: playback.isPlaying,
  });

  const loadingPlayersToRender = useMemo(() => buildLoadingPlayers(), []);
  const { teamColors, isTeamColorsReady } = useReplayTeamColors(replay.teamStyles);
  const isVisualizationReady = isTeamColorsReady && Boolean(replay.replayData && characterStates.length > 0);

  const { ballPosition: ballFromReplay } = getBallPosition(replay.replayData, playback.progress);

  const playersToRender = useMemo(() => {
    if (!replay.replayData) {
      return loadingPlayersToRender;
    }

    if (characterStates.length > 0) {
      return characterStates.map(char => {
        const { x, y } = gameToScreenCoords(char.x, char.y);
        return { ...char, x, y };
      });
    }

    return [];
  }, [replay.replayData, characterStates, loadingPlayersToRender]);

  const ballPosition = useMemo(() => {
    if (ballFromReplay) {
      return ballFromReplay;
    }

    return null;
  }, [ballFromReplay, replay.replayData]);

  const currentDecision = replay.decisions[playback.highlightPointIndex] ?? null;
  const mergedStageOptions = useMemo(
    () => ({
      ...defaultStageOptions,
      ...stageOptions,
    }),
    [defaultStageOptions, stageOptions]
  );

  useEffect(() => {
    if (!onReadyChange) {
      return;
    }

    if (!isVisualizationReady) {
      onReadyChange(false);
      return;
    }

    let frameOne = 0;
    let frameTwo = 0;

    frameOne = window.requestAnimationFrame(() => {
      frameTwo = window.requestAnimationFrame(() => {
        onReadyChange(true);
      });
    });

    return () => {
      if (frameOne) {
        window.cancelAnimationFrame(frameOne);
      }
      if (frameTwo) {
        window.cancelAnimationFrame(frameTwo);
      }
    };
  }, [isVisualizationReady, onReadyChange]);

  useEffect(() => {
    return () => {
      onReadyChange?.(false);
    };
  }, [onReadyChange]);

  return (
    <Stage width={VIEWPORT_DIMENSIONS.WIDTH} height={VIEWPORT_DIMENSIONS.HEIGHT} options={mergedStageOptions}>
      {isTeamColorsReady ? (
        <>
          <FootballPitch teamColors={teamColors} />

          <HeatmapRenderer characterStates={characterStates} showHeatmap={showHeatmap} currentTime={playback.progress} teamColors={teamColors} />

          {playersToRender.map(character => (
            <Player key={character.id} character={character} showVisionCone={showVisionCone} teamColors={teamColors} />
          ))}

          {showPassVisualization && replay.replayData ? (
            <PassVisualization
              currentDecision={currentDecision}
              currentTime={playback.progress}
              selectedCard={playback.selectedCard}
              replayData={replay.replayData}
            />
          ) : null}

          <Ball ballPosition={ballPosition} />
        </>
      ) : null}
    </Stage>
  );
};

export default ReplayPixiStage;
